/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import SociollaText from '@components/sociolla/SociollaText';
import MoreButton from '@components/navbar/MoreButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import * as Layout from '@components/screen/Layout';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import Image from '@components/global/Image';
import Button from '@components/form/Button';
import StatusBadge from '@components/global/StatusBadge';

import { hasNoAccess, parseDate, exportCsvApi } from '@global/helpers';
import Colors from '@global/colors';
import { isWeb } from '@global/screens';
import ModalCreateRequestProductListing from './components/ModalCreateRequestProductListing';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetRequestProductListingListQuery,
  useGetRequestProductListingCountQuery,
  useGetRequestProductListingInfiniteListInfiniteQuery,
} from '@api/listApi';

const tableStructure = {
  customer_name: {
    head: 'Customer Name',
    body: 'customer.name',
  },
  customer_code: {
    head: 'Customer Code',
    body: 'customer.code',
  },
  is_active: {
    head: 'Is Active',
    body: 'is_active',
    type: 'status_active',
  },
  updated_at: {
    head: 'Last Update',
    body: 'updated_at',
    type: 'slot',
  },
};

// The SAME route is rendered per year (from RequestProductPeriodScreen), so the
// scope must include the year — otherwise different years share one params bucket.
// `year` is injected into the API filter here (it isn't a user-editable field).
const toFilter = (rawFilter = {}, year) => {
  const filter = { year };
  const stored = rawFilter?.keyword;
  const keyword = stored?.$regex ?? stored;
  if (keyword) {
    filter.keyword = keyword;
  }
  return filter;
};

function RequestProductListingScreen(props) {
  // ── local state ──
  const [showAdd, setShowAdd] = useState(false);

  // ── derived ──
  const year = props?.route?.params?.year;

  const hasAccessCreate = !hasNoAccess([
    'superadmin',
    'general',
    'general_manager_marketing',
  ]);

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetRequestProductListingListQuery,
    countHook: useGetRequestProductListingCountQuery,
    infiniteHook: useGetRequestProductListingInfiniteListInfiniteQuery,
    toFilter: (raw) => toFilter(raw, year),
    scopeKey: `RequestProductListing-${year}`,
  });

  // ── memoized values ──
  const searchValue = useMemo(() => {
    const k = params.filter?.keyword;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map((item) => (item._id ? { ...item, id: item._id } : item));
  }, [list.data]);

  // ── effects ──
  useEffect(() => {
    try {
      const menus = [];
      menus.push({
        type: 'function',
        name: 'Export',
        callback: onExport,
      });
      if (hasAccessCreate) {
        menus.push({
          type: 'function',
          name: 'Add',
          callback: handleAddModal,
        });
      }
      props.navigation.setOptions({
        headerRight: () => {
          return isWeb ? (
            <NotificationSettingButton {...props} />
          ) : (
            <MoreButton menu={menus} {...props} />
          );
        },
      });
    } catch (error) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation, hasAccessCreate]);

  // ── handlers ──
  const handleDetail = (data) => {
    openChild('RequestProductListingDetailScreen', {
      titleScreen: data.customer.code,
      name: data.customer.code,
      id: data._id,
      year: `${data.year}`,
      go_back_key: props.route.key,
    });
  };

  const handleAdd = () => {
    setShowAdd(false);
    openChild('RequestProductListingFormScreen', {
      year: `${year}`,
      go_back_key: props.route.key,
    });
  };

  const handleAddModal = () => {
    setShowAdd(true);
  };

  const onExport = async () => {
    await exportCsvApi(
      'request-product-listing',
      { year },
      {
        isDownload: false,
        message: 'Export in progress, the result will be sent to your email.',
      },
    );
  };

  const handleCloseAddModal = () => {
    setShowAdd(false);
  };

  const goBack = () => {
    props.navigation.replace('RequestProductListingScreen', {});
    props.navigation.navigate('RequestProductPeriodScreen');
  };

  // ── render ──
  return (
    <Layout.Container>
      {isWeb ? (
        <Layout.Wrapper white={false}>
          <Layout.Back
            title="Product Listing & Summary"
            onCallback={() => goBack()}
          />
        </Layout.Wrapper>
      ) : (
        false
      )}
      {isWeb && (
        <Layout.Title title={year} style={{ zIndex: 9 }}>
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
            }}>
            <Button
              type="outline-primary"
              rounded={true}
              buttonStyle={{ width: 90, marginRight: 10 }}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.blue1 }}>
                  Export
                </SociollaText>
              }
              onPress={onExport}
            />
            {hasAccessCreate ? (
              <Button
                type="primary"
                rounded={true}
                buttonStyle={{ width: 90 }}
                title={
                  <SociollaText
                    type="medium"
                    style={{ fontSize: 14, color: Colors.white }}>
                    + Add
                  </SociollaText>
                }
                onPress={handleAddModal}
              />
            ) : (
              false
            )}
          </View>
        </Layout.Title>
      )}
      {isWeb ? (
        <Table
          filterName={scope}
          name="request-product-listing"
          structures={tableStructure}
          loading={list.isLoading}
          refetching={list.isFetching}
          data={rows}
          searchKey="keyword"
          searchPlaceholder="Search by name, code..."
          searchValue={searchValue}
          hasNoHide={true}
          hasCallback={true}
          totalData={count.data}
          skip={params.skip}
          limit={params.limit}
          onClickRow={({ data }) => handleDetail(data)}
          showFilter={false}
          slot={{
            updated_at: ({ data, index }) => (
              <SociollaText key={`reference-${index}`}>
                {parseDate(data.updated_at, 'd MMMM yyyy')} |{' '}
                {data.updated_by?.name || ''}
              </SociollaText>
            ),
          }}
        />
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="keyword"
          placeholder="Search by"
          searchValue={searchValue}
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          showSearch={true}
          renderItem={({ data, index }) => (
            <TouchableOpacity
              activeOpacity={0.6}
              style={styles.item}
              onPress={() => handleDetail(data)}
              key={index}>
              <View style={[styles.itemSection, { alignItems: 'flex-start' }]}>
                <View style={{ width: '70%' }}>
                  <SociollaText type="medium">
                    {data.customer.name}
                  </SociollaText>
                  <SociollaText>{data.customer.code}</SociollaText>
                </View>
                <StatusBadge status={data.is_active ? 'active' : 'in_active'} />
              </View>
              <View style={styles.itemSection}>
                <View>
                  <SociollaText>Last Updated:</SociollaText>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginTop: 5,
                    }}>
                    <Image
                      style={{
                        width: 23,
                        height: 16,
                        marginRight: 5,
                      }}
                      width={23}
                      height={16}
                      svg={true}
                      source={require('@assets/img/icons/menu/calendar.svg')}
                    />
                    <SociollaText style={{ fontSize: 12, color: Colors.grey2 }}>
                      {data.updated_at
                        ? `${parseDate(data.updated_at, 'd MMMM yyyy')} | ${
                            data.updated_by?.name || ''
                          }`
                        : '-'}
                    </SociollaText>
                  </View>
                </View>
              </View>
            </TouchableOpacity>
          )}
        />
      )}

      <ModalCreateRequestProductListing
        show={showAdd}
        onClose={handleCloseAddModal}
        handleAdd={handleAdd}
        year={year}
      />
    </Layout.Container>
  );
}

const styles = StyleSheet.create({
  item: {
    width: '100%',
    paddingVertical: 20,
    paddingHorizontal: 20,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  itemSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

export default RequestProductListingScreen;
